import React, { useRef, useEffect, useState, useCallback } from 'react';
import { StyleSheet, Pressable, Animated, View, Platform, Easing } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import useTranslation from '@/hooks/useTranslation';
import { lightTap } from '@/constants/utils/haptics';
import dictateModeService, { DICTATE_STATES, MAX_RECORDING_DURATION_MS } from '@/lib/dictateMode';
import DictateWaveform from './DictateWaveform';

const REMAINING_WARNING_MS = 30000;
const isWeb = Platform.OS === 'web';

function formatElapsed(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Pulsing "live" dot shown while the mic is capturing. */
function RecordingDot({ color }) {
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 700, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 700, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  return (
    <View style={styles.dotWrap}>
      <Animated.View
        style={[
          styles.dotHalo,
          {
            backgroundColor: color,
            opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0] }),
            transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 2.1] }) }],
          },
        ]}
      />
      <View style={[styles.dot, { backgroundColor: color }]} />
    </View>
  );
}

function Spinner({ color, size = 14, borderWidth = 2 }) {
  const spin = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(spin, { toValue: 1, duration: 900, easing: Easing.linear, useNativeDriver: true })
    );
    loop.start();
    return () => loop.stop();
  }, [spin]);

  return (
    <Animated.View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth,
        borderColor: 'transparent',
        borderTopColor: color,
        borderRightColor: color,
        transform: [{ rotate: spin.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }) }],
      }}
    />
  );
}

function DictateRecordingOverlay({ dictateState, onCancel, onSend }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [audioLevels, setAudioLevels] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const enterAnim = useRef(new Animated.Value(0)).current;

  const isStarting = dictateState === DICTATE_STATES.STARTING;
  const isRecording = dictateState === DICTATE_STATES.RECORDING;
  const isProcessing = dictateState === DICTATE_STATES.PROCESSING;
  const isBusy = isStarting || isProcessing;
  const remainingMs = Math.max(0, MAX_RECORDING_DURATION_MS - elapsedMs);
  const showRemainingWarning = isRecording && remainingMs <= REMAINING_WARNING_MS;

  // Subscribe to audio levels + duration from the dictation service
  useEffect(() => {
    const previousAudioLevel = dictateModeService.onAudioLevel;
    const previousDurationTick = dictateModeService.onDurationTick;
    dictateModeService.onAudioLevel = setAudioLevels;
    dictateModeService.onDurationTick = setElapsedMs;
    return () => {
      dictateModeService.onAudioLevel = previousAudioLevel;
      dictateModeService.onDurationTick = previousDurationTick;
    };
  }, []);

  useEffect(() => {
    Animated.spring(enterAnim, {
      toValue: 1,
      friction: 9,
      tension: 120,
      useNativeDriver: true,
    }).start();
  }, [enterAnim]);

  const handleCancel = useCallback(() => {
    if (isProcessing) return;
    lightTap();
    onCancel?.();
  }, [onCancel, isProcessing]);

  const handleSend = useCallback(() => {
    if (!isRecording) return;
    lightTap();
    onSend?.();
  }, [onSend, isRecording]);

  // Keyboard on web: Enter finishes, Esc cancels. The textarea is hidden
  // while dictating, so these do not compete with typing.
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      } else if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleSend();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleCancel, handleSend]);

  const statusLabel = isStarting
    ? t('chatInput.dictate.starting')
    : isProcessing
      ? t('chatInput.dictate.transcribing')
      : null;

  const timerLabel = showRemainingWarning
    ? t('chatInput.dictate.timeLeft', { time: formatElapsed(remainingMs) })
    : formatElapsed(elapsedMs);

  const sendLabel = isStarting
    ? t('chatInput.dictate.starting')
    : isProcessing
      ? t('chatInput.dictate.transcribing')
      : t('chatInput.dictate.finish');

  return (
    <Animated.View
      style={[
        styles.container,
        {
          backgroundColor: colors.solid_color_tertiary,
          opacity: enterAnim,
          transform: [
            { scale: enterAnim.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1] }) },
            { translateY: enterAnim.interpolate({ inputRange: [0, 1], outputRange: [4, 0] }) },
          ],
        },
      ]}
      accessibilityRole="toolbar"
      accessibilityLabel={isRecording ? t('chatInput.dictate.listening') : statusLabel}
    >
      <Tooltip label={t('chatInput.dictate.cancel')}>
        <Pressable
          style={({ hovered, pressed }) => [
            styles.roundButton,
            {
              backgroundColor: hovered || pressed
                ? colors.solid_color_secondary_dark
                : colors.solid_color_secondary,
              opacity: isProcessing ? 0.45 : 1,
            },
          ]}
          onPress={handleCancel}
          disabled={isProcessing}
          accessibilityLabel={t('chatInput.dictate.cancel')}
          accessibilityRole="button"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Icon name="xmark" solid size={14} color={colors.text_color_primary} />
        </Pressable>
      </Tooltip>

      <View style={styles.meta} accessibilityLiveRegion="polite">
        {isRecording ? (
          <>
            <RecordingDot color={colors.color_destructive} />
            <Text
              size="xs"
              weight="medium"
              color={showRemainingWarning ? 'danger' : 'secondary'}
              style={styles.timer}
              numberOfLines={1}
              accessibilityLabel={`${t('chatInput.dictate.listening')} ${timerLabel}`}
            >
              {timerLabel}
            </Text>
          </>
        ) : (
          <>
            <Spinner color={colors.text_color_tertiary} />
            <Text size="xs" weight="medium" color="secondary" numberOfLines={1} style={styles.status}>
              {statusLabel}
            </Text>
          </>
        )}
      </View>

      <DictateWaveform
        audioLevels={audioLevels}
        isProcessing={isProcessing}
        isStarting={isStarting}
      />

      <Tooltip label={isWeb && isRecording ? `${sendLabel} · ${t('chatInput.dictate.finishHint')}` : sendLabel}>
        <Pressable
          style={({ hovered, pressed }) => [
            styles.roundButton,
            {
              backgroundColor: colors.solid_color_toast,
              opacity: isBusy ? 0.7 : hovered || pressed ? 0.88 : 1,
            },
          ]}
          onPress={handleSend}
          disabled={isBusy}
          accessibilityLabel={sendLabel}
          accessibilityRole="button"
          accessibilityState={{ disabled: isBusy, busy: isBusy }}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          {isBusy ? (
            <Spinner color={colors.text_color_alt} size={18} />
          ) : (
            <Icon name="arrow-up" size={16} color={colors.text_color_alt} />
          )}
        </Pressable>
      </Tooltip>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    height: 46,
    borderRadius: 23,
    paddingHorizontal: 6,
    gap: 8,
    overflow: 'hidden',
  },
  roundButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'background-color 0.15s ease, opacity 0.15s ease' }),
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  dotWrap: {
    width: 12,
    height: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dotHalo: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  timer: {
    minWidth: 34,
    fontVariant: ['tabular-nums'],
  },
  status: {
    maxWidth: 120,
  },
});

export default React.memo(DictateRecordingOverlay);
